import { DownloadOutlined, InfoCircleOutlined, MailOutlined } from '@ant-design/icons';
import { Breadcrumb, ConfigProvider, Empty, Form, Select, Space, Tooltip } from 'antd';
import { Store } from 'antd/lib/form/interface';
import locale from 'antd/lib/locale/en_US';
import { lowerCase } from 'lodash';
import moment from 'moment';
import { httpCreators } from '@ppvrbh/merchant-frontend-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { generalCreators } from '../../../redux/general/general-action';
import { DATE_FORMAT_YEAR_START_NO_TZ, DATE_PICKER_FORM, DEFAULT_PAGE_SIZE, MODAL_TYPE } from '../../../redux/general/general-constant';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../redux/general/general-reducer';
import { RBHAlert, RBHBox, RBHButton, RBHDatePicker, RBHSelect, RBHTable, RBHText } from '../../../shared/components';
import styleImage from '../../../shared/components/rbh-image/styles';
import styleText from '../../../shared/components/rbh-text/styles';
import { UtilHelper } from '../../../shared/helpers/utility';
import { Fonts } from '../../../shared/theme';
import commonStyle from '../../../shared/theme/common-style';
import metrics from '../../../shared/theme/metrics';
import { mainCreators } from '../../main-action';
import { MAIN_STATE_KEY } from '../../main-reducer';
import { reportCreators } from './redux/report-action';
import { BATCH_REPORT_END, BATCH_REPORT_START, REPORT_REQUEST_ID_LIST, T_DATE_SELECT } from './redux/report-constant';
import { REPORT_ALERT, REPORT_STATE_KEY, reportStateSelector } from './redux/report-reducer';
import { reportSagas } from './redux/report-saga';
import { ReportService } from './redux/report-service';
import './style.css';
import styles from './styles';

export const ReportScreen: React.FC<Props> = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const reportData = useSelector(reportStateSelector(REPORT_STATE_KEY.REPORT_LIST)) as [];
  const reportResp = useSelector(reportStateSelector(REPORT_STATE_KEY.REPORT_RESPONSE));
  const shopList = useSelector(reportStateSelector(REPORT_STATE_KEY.SHOP_LIST)) || [];
  const reportTypeSelected = useSelector(reportStateSelector(REPORT_STATE_KEY.REPORT_TYPE_SELECTED));
  const defaultCalendar = moment().subtract(T_DATE_SELECT, 'd');
  const [saveDate, setSaveDate] = useState<string>(defaultCalendar.format(DATE_FORMAT_YEAR_START_NO_TZ));
  const alertState = useSelector(reportStateSelector(REPORT_STATE_KEY.ALERT_STATE));
  const alertCountdown = useSelector(reportStateSelector(REPORT_STATE_KEY.ALERT_COUNTDOWN));
  const remoteConfig = useSelector(generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG)) as Array<{ group: string, key: string, value: string }>;
  const manageEmailBtn = remoteConfig?.filter((config: { key: string; }) => config.key === 'report.additional.email.button.enable')?.[0]?.value;
  const reportTypeBtn = remoteConfig?.filter((config: { key: string; }) => config.key === 'report.reporttype.button.enable')?.[0]?.value;
  const batchReportStart = remoteConfig?.filter((config: { key: string; }) => config.key === 'batch.report.start')?.[0]?.value || BATCH_REPORT_START;
  const batchReportEnd = remoteConfig?.filter((config: { key: string; }) => config.key === 'batch.report.end')?.[0]?.value || BATCH_REPORT_END;
  const [offset, setOffset] = useState<number>(0);
  const limit = DEFAULT_PAGE_SIZE;
  const total = useMemo(() => {
    return reportResp?.resultInfo?.totalCount;
  }, [reportResp?.resultInfo?.totalCount])
  const [shopId, setShopId] = useState<number | string>('all');
  const shopListOption = useMemo(() => {
    const allBranchOption = { shopId: 'all', shopName: 'ทุกสาขา' };
    return [allBranchOption, ...shopList];
  }, [shopList]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const getShopIdParam = useCallback((value: string | number) => {
    return (typeof (value) === 'number') ? value : undefined
  }, []);

  const onFailureCallback = () => {
    dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
      visible: true,
      onOk: () => window.location.reload(),
      type: MODAL_TYPE.ERROR,
      clickClosable: false,
      escClosable: false,
    }));
  }
  useEffect(() => {
    dispatch(mainCreators.setMainState(MAIN_STATE_KEY.HEADER_MENU, 6));
    dispatch(reportCreators.callConfigAndShopList(resetReload, onFailureCallback))
  }, []);

  useEffect(() => {
    let closeAlertTimer: any;
    if (alertCountdown || (!alertCountdown && alertState === REPORT_ALERT.ALERT_REPORT_TYPE_FAIL)) {
      closeAlertTimer = setTimeout(() => { handleCloseAlert(); }, 3000);
    }
    return () => {
      clearTimeout(closeAlertTimer);
    };
  }, [alertCountdown]);

  const pushToMain = () => {
    history.push('/main');
  };

  const callReport = (dataParams: { date: string, offset: number, limit: number, shopId?: number | string }, onSuccessChangePage?: () => void, onFailureChangePage?: () => void) => {
    dispatch(httpCreators.request({
      id: REPORT_REQUEST_ID_LIST.GET_REPORT,
      sagaHandler: reportSagas.getReportHandler,
      callbackParams: { resetReload, onSuccessChangePage, onFailureChangePage },
      service: ReportService.getReport,
      serviceParams: dataParams
    }));
  };

  const resetReload = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const date = defaultCalendar.format(DATE_FORMAT_YEAR_START_NO_TZ);
    setSaveDate(date);
    setShopId('all');
    setOffset(0);
    const dataParams = {
      date,
      offset: 0,
      limit
    }
    callReport(dataParams);
    form.resetFields();
  };

  const getDownload = (date: any, docToken: string) => {
    dispatch(httpCreators.request({
      id: REPORT_REQUEST_ID_LIST.GET_DOWNLOAD,
      sagaHandler: reportSagas.getDownloadHandler,
      callbackParams: resetDownload,
      service: ReportService.getDownload,
      serviceParams: { date, docToken }
    }));
  };

  const resetDownload = () => {
    const dataParams = {
      date: saveDate,
      offset,
      limit,
      shopId
    };
    callReport(dataParams);
  };

  const isAlert = () => {
    if (alertState === REPORT_ALERT.ALERT_REPORT_TYPE_SUCCESS) {
      return (
        <RBHBox id={'pickReport-alertSuccess'} data-testid={'pickReport-alertSuccess'}>
          <RBHAlert type={'success'} text={'main.report.pickReport.alert.success'} afterClose={handleCloseAlert}
            values={{ reportTypeSelected }} /><br />
        </RBHBox>
      );
    } else if (alertState === REPORT_ALERT.ALERT_REPORT_TYPE_FAIL) {
      return (
        <RBHBox id={'pickReport-alertFail'} data-testid={'pickReport-alertFail'}>
          <RBHAlert type={'error'} text={'main.report.pickReport.alert.fail'} afterClose={handleCloseAlert} /><br />
        </RBHBox>
      );
    }
  };

  const handleCloseAlert = () => {
    dispatch(reportCreators.setReportState(REPORT_STATE_KEY.ALERT_STATE, REPORT_ALERT.ALERT_BLANK));
  };

  const BreadcrumbRender = () => {
    return (
      <RBHBox>
        <Breadcrumb style={styleText.breadCrumb}>
          <Breadcrumb.Item>
            <a data-testid='id-breadcrumb-to-home' id='id-breadcrumb-to-home' style={styleText.breadCrumbItemLink}
              onClick={pushToMain}>
              <FormattedMessage id={'main.main'} />
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span data-testid='id-breadcrumb-report' id='id-breadcrumb-report' style={styleText.breadCrumbItem}>
              <FormattedMessage id={'main.report'} />
            </span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </RBHBox>
    );
  };

  const disabledDate = (current: any) => {
    const start = moment().subtract(T_DATE_SELECT, 'd');
    return current > start;
  };

  const headerRender = () => {
    return (
      <RBHBox style={styles.header}>
        <RBHText data-testid='id-report' id='id-report' style={styleText.headerText} text={'main.report'} />
        <RBHBox>
          <Space size={metrics.baseMargin} align={'end'}>
            {reportTypeBtn === 'true' &&
              <RBHButton data-testid={'pickReport-btn'} id={'pickReport-btn'}
                style={styles.pickReportButton} text={'main.report.pickReport.btn'} type='primary'
                onClick={() =>
                  dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
                    visible: true,
                    clickClosable: false,
                    escClosable: false,
                    batchReportStart,
                    batchReportEnd,
                    merchantReportType: reportResp.merchantReportType,
                    isBatchTimePeriod: reportResp.isBatchTimePeriod,
                    type: MODAL_TYPE.PICK_REPORT
                  }))
                }>
              </RBHButton>
            }
            {manageEmailBtn === 'true' &&
              <RBHButton data-testid={'manage-report-email-btn'} id={'manage-report-email-btn'}
                style={styles.buttonAdd} text={'main.report.manage.report.email.btn'} type='primary'
                onClick={() => history.push('/main/report/manage_email')} icon={<MailOutlined translate={null} />}>
              </RBHButton>
            }
          </Space>
        </RBHBox>
      </RBHBox>
    );
  };

  const headerDesc = () => {
    return (
      <RBHBox style={styles.textDesc}>
        <RBHText data-testid='id_report_desc1' id='id_report_desc1' style={styleText.formText}
          text={'main.report_desc1'} />&nbsp;
        <RBHText data-testid='id_report_desc2' id='id_report_desc2' style={styles.text14RegularPurple}
          text={'main.report_desc2'} />
      </RBHBox>
    );
  };

  const onFinish = (values: Store) => {
    const date = moment(values.calendarInput).format(DATE_FORMAT_YEAR_START_NO_TZ);
    setSaveDate(date);
    setShopId(values.shopIdOption);
    setCurrentPage(1);
    const dataParams = {
      date,
      offset: 0,
      limit,
      shopId: getShopIdParam(values.shopIdOption)
    }
    callReport(dataParams);
  }

  const inputForm = () => {
    return (
      <RBHBox style={styles.formWrapper}>
        <Form size='large' form={form} onFinish={onFinish}>
          <RBHBox style={styles.inputContainer}>
            <RBHBox style={commonStyle.marginRight}>
              <RBHText data-testid='id_report_form_date' id='id_report_form_date' style={styles.formDateTextRegular14Gray} text={'main.report_form_date'} />
              <ConfigProvider locale={locale}>
                <Form.Item name='calendarInput' label='' colon={false} validateTrigger={['onChange', 'onBlur']}
                  initialValue={defaultCalendar} style={styles.formItemDate}>
                  <RBHDatePicker
                    allowClear={false}
                    showToday={false}
                    name={'calendarInput'}
                    style={styles.calendarInput}
                    data-testid={'calendarInput'}
                    id={'calendarInput'}
                    format={DATE_PICKER_FORM}
                    autoComplete='off'
                    disabledDate={disabledDate} />
                </Form.Item>
              </ConfigProvider>
            </RBHBox>
            <RBHBox style={commonStyle.marginRight}>
              <RBHText data-testid='id_report_form_branch' id='id_report_form_branch' style={styles.formDateTextRegular14Gray} text={'main.report_form_branch'} />
              <Form.Item noStyle name='shopIdOption' initialValue={<FormattedMessage id={'main.report_option_all'} />}>
                <RBHSelect optionFilterProp='children' style={styles.formItemBranch}>
                  {
                    shopListOption?.map((value: { shopName: string, shopId: number }, index: number) => {
                      return (
                        <Select.Option
                          key={index}
                          id={`branchOption-${index}`}
                          data-testid={`branchOption-${index}`}
                          value={value?.shopId}
                          style={Fonts.style.textBodyRegularFakeBlack14}
                          title={value?.shopName}
                        >
                          {value?.shopName}
                        </Select.Option>
                      )
                    })
                  }
                </RBHSelect>
              </Form.Item>
            </RBHBox>
            <RBHBox style={styles.serachButtonContainer}>
              <RBHButton id='report_search_button' data-testid='report_search_button' style={styles.searchButton} type='primary' htmlType='submit' text='main.report_button_search' />
            </RBHBox>
          </RBHBox>
        </Form>
      </RBHBox>
    );
  };

  const tooltipText = () => {
    return (
      <RBHBox style={styles.tooltipText} id={'tooltip-box'}>
        <FormattedMessage id={'main.report.tooltip.header'} /><br />
        <FormattedMessage id={'main.report.tooltip.content1'} /><br />
        <FormattedMessage id={'main.report.tooltip.content2'} /><br />
        <FormattedMessage id={'main.report.tooltip.content3'} /><br />
        <FormattedMessage id={'main.report.tooltip.content4'} /><br />
        <FormattedMessage id={'main.report.tooltip.content5'} />
      </RBHBox>
    );
  };
  const tooltipReportType = () => {
    return (
      <RBHBox style={styles.tooltipTextNextLine} id={'tooltip-box'}>
        <FormattedMessage id={'main.report.tooltip.report_type_header'} /><br />
        <FormattedMessage id={'main.report.tooltip.report_type1'} /><br />
        <FormattedMessage id={'main.report.tooltip.report_type2'} /><br />
      </RBHBox>
    );
  };

  const truncateString = (text: string, length: number) => {
    return text?.length > length ? text.substring(0, length) + '...' : text;
  };

  const columns = [
    {
      width: metrics.tableHeader.width120,
      title: <FormattedMessage id={'main.report.table.column1'} />,
      render: (value: any, all: any, index: number) => {
        const reportDate = 'reportDate-' + index;
        return (
          <RBHBox id={`reportDate-${index}`} key={index}>
            <RBHText id={reportDate} style={styleText.textRegularFakeBlack}>
              {UtilHelper.formatThaiDate(saveDate)}
            </RBHText>
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width140,
      title: <FormattedMessage id={'main.report.table.column2'} />,
      render: (value: any, all: any, index: number) => {
        const vatAddressBranchNo = 'vatAddressBranchNo-' + index;
        return (
          <RBHBox id={`vatAddressBranchNo-${index}`} key={index}>
            <RBHText id={vatAddressBranchNo} style={styleText.textRegularFakeBlack}>
              {all.vatAddressBranchNo}
            </RBHText>
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width140,
      title: <FormattedMessage id={'main.report.table.column3'} />,
      render: (value: any, all: any, index: number) => {
        const shopName = 'shopName-' + index;
        const shopBranchCode = 'branchCode-' + index;
        return (
          <RBHBox id={`shops-${index}`} key={index} style={{ width: '108px' }}>
            <RBHText id={shopName} style={styleText.textRegularFakeBlack}>
              {all.shopName}
            </RBHText>
            <RBHText id={shopBranchCode} style={styleText.textRegularGray}>
              {all.branchCode}
            </RBHText>
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width130,
      title: <RBHBox>
        <FormattedMessage id={'main.report.table.column4'} />&nbsp;&nbsp;
        <Tooltip id='ant-tooltip-report-type-inner' title={tooltipReportType} placement={'bottomRight'}>
          <InfoCircleOutlined translate={null} />
        </Tooltip>
      </RBHBox>,
      render: (value: any, all: any, index: number) => {
        return (
          <RBHBox key={index}>
            <RBHText id={`reportType-${index}`} style={styleText.textRegularFakeBlack}>
              {all.reportType}
            </RBHText>
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width140,
      title: <FormattedMessage id={'main.report.table.column5'} />,
      render: (value: any, all: any, index: number) => {
        return (
          <RBHBox id={`documentType-${index}`} key={index}>
            <RBHText id={`mapDocumentType-${index}`} style={styleText.textRegularFakeBlack} text={`main.report.type.${lowerCase(all.documentType)}`} />
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width160,
      title: <RBHBox>
        <FormattedMessage id={'main.report.table.column6'} />&nbsp;&nbsp;
        <Tooltip id='ant-tooltip-report-inner' title={tooltipText} placement={'bottomRight'}>
          <InfoCircleOutlined translate={null} />
        </Tooltip>
      </RBHBox>,
      render: (value: any, all: any, index: number) => {
        const reportName = 'reportName-' + index;
        return (
          <RBHBox id={`reportName-${index}`} key={index}>
            <RBHText id={reportName} style={styleText.textRegularFakeBlack}>
              {truncateString(all.documentCode, 14) + '.' + all.fileFormat}
            </RBHText>
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width90,
      title: <FormattedMessage id={'main.report.table.column7'} />,
      dataIndex: 'documentToken',
      render: (value: any, all: any, index: number) => {
        const downloadReport = 'downloadReport-' + index;
        return (
          <RBHBox style={{ textAlign: 'center' }} key={index} onClick={() => getDownload(saveDate, all.documentToken)}>
            <DownloadOutlined data-testid={downloadReport} id={downloadReport} translate={null} style={styleImage.iconTable} />
          </RBHBox>
        );
      }
    }
  ];

  const onChangePage = (page: number) => {
    const currentOffset = (page - 1) * limit;
    const onSuccessChangePage = () => {
      setCurrentPage(page);
      setOffset(currentOffset);
      // reset value in form to current selected to prevent confusion when changing value in form and change page without clicking submit button
      form.setFieldsValue({
        calendarInput: moment(saveDate),
        shopIdOption: shopId
      })
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const onFailureChangePage = () => onChangePage(page);
    const dataParams = {
      date: saveDate,
      offset: currentOffset,
      limit,
      shopId: getShopIdParam(shopId)
    }
    callReport(dataParams, onSuccessChangePage, onFailureChangePage);
  }

  const tableRender = () => {
    return (
      <RBHBox style={styles.defaultContent}>
        <RBHTable
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span> <FormattedMessage id={'main.table.notFound'} /> </span>} />
          }}
          rowKey={'documentCode'}
          columns={columns}
          dataSource={reportData}
          pagination={{
            style: styleText.formText,
            locale: { items_per_page: '/ หน้า' },
            showSizeChanger: false,
            onChange: onChangePage,
            current: currentPage,
            pageSize: DEFAULT_PAGE_SIZE,
            total,
            position: ['bottomLeft'],
            responsive: true,
            showTotal: (total: number, range: number[]) => {
              return (
                <RBHText
                  style={styles.paginationText}
                  id={'report-pagination-text'}
                  data-testid={'report-pagination-text'}
                  text={'main.report.pagination'}
                  values={{
                    range1: range[0],
                    range2: range[1],
                    total
                  }}
                />
              );
            }
          }}
        >
        </RBHTable>
      </RBHBox>
    );
  };

  return (
    <RBHBox>
      {isAlert()}
      {BreadcrumbRender()}
      <RBHBox>
        {headerRender()}
        {headerDesc()}
        {inputForm()}
        <RBHBox style={styles.paddingTop20}>
          {tableRender()}
          <RBHText data-testid='id_report_remark' id='id_report_remark' style={styles.remarkTextRegular14Gray}
            text={'main.report.remark'} values={{ date: T_DATE_SELECT }} />
        </RBHBox>
      </RBHBox>
    </RBHBox>
  );
};

type Props = {};
